import {
  AutomatedActions,
  AutomatedActionType,
  AutomationFlowJourneySteps,
  AutomationStateType,
  DisabledEntityType,
  PerformedActionsOptions,
  PerformedActionType,
  AutomationSortOrderType,
  AutomationTemplateType
} from '../types/automations'
import { CartConditionOptions, PurchaseOptions } from '../types/common/cartConditions'
import {
  CustomerEligibilityErrorType,
  CustomerEligibilityOptions,
  CustomerEligibilityStateType,
  RtoRiskStrategyOptions,
  utmDataOptions
} from '../types/common/customerConditions'
import { FlowErrorType } from '../types/common/error'
import { parseCartConditionsResponse } from './cartConditionsHelpers'

export const getLabel = (type: DisabledEntityType) => {
  const automationLabel = {
    DISABLE_PAYMENT: 'Payments',
    DISABLE_REWARDS: 'Disable Rewards',
    NONE: 'None'
  } as const

  return automationLabel[type] || type
}

const getAutomationType = (type: AutomatedActions) => {
  switch (type) {
    case AutomatedActions.DISABLE_REWARDS:
      return 'DISABLE_REWARDS'
    case AutomatedActions.HIDE_PAYMENT_MODES:
      return 'DISABLE_PAYMENT'
  }
}

const getDisabledEntities = (state: AutomationStateType) => {
  switch (state.automatedAction.type) {
    case AutomatedActions.HIDE_PAYMENT_MODES:
      return state.automatedAction.paymentMethodIds
    case AutomatedActions.DISABLE_REWARDS:
      return ['LOYALTY', 'REWARDS_WALLET']
  }
}

export const getAutomationsUTMStateKey = (key: string): utmDataOptions => {
  switch (key) {
    case 'utm_source':
      return utmDataOptions.UTM_SOURCE
    case 'utm_campaign':
      return utmDataOptions.UTM_CAMPAIGN
    case 'utm_medium':
      return utmDataOptions.UTM_MEDIUM
    case 'utm_content':
      return utmDataOptions.UTM_CONTENT
    default:
      return utmDataOptions.UTM_SOURCE
  }
}

export const getUTMStateKey = (key: string): utmDataOptions => {
  switch (key) {
    case 'source':
      return utmDataOptions.UTM_SOURCE
    case 'campaign':
      return utmDataOptions.UTM_CAMPAIGN
    case 'medium':
      return utmDataOptions.UTM_MEDIUM
    case 'content':
      return utmDataOptions.UTM_CONTENT
    default:
      return utmDataOptions.UTM_SOURCE
  }
}

const getExecutionConditions = (state: AutomationStateType) => {
  const executionConditions = []
  if (state.cartConditions.type !== CartConditionOptions.NO_CONDITIONS) {
    if (state.cartConditions.selectedOption.type === PurchaseOptions.ENTIRE_CART) {
      executionConditions.push({
        type: 'CART_CONDITIONS',
        ...(state.cartConditions.type === CartConditionOptions.PURCHASE_AMOUNT
          ? {
              minimum_amount: state.cartConditions.selectedOption.minimumValue,
              maximum_amount: state.cartConditions.selectedOption.maximumValue || null
            }
          : {
              minimum_quantity: state.cartConditions.selectedOption.minimumValue,
              maximum_quantity: state.cartConditions.selectedOption.maximumValue || null
            })
      })
    }
    if (state.cartConditions.selectedOption.type !== PurchaseOptions.ENTIRE_CART) {
      executionConditions.push({
        type: 'CART_CONDITIONS',
        prerequisites: state.cartConditions.selectedOption.quantityDistribution.quantityDistributionItems.map(item => {
          return {
            ...(state.cartConditions.type === CartConditionOptions.PURCHASE_AMOUNT
              ? {
                  price: item.minimumValue,
                  max_price: item.maximumValue || null
                }
              : {
                  quantity: item.minimumValue,
                  max_quantity: item.maximumValue || null
                }),
            ...(item.catalogType === PurchaseOptions.SPECIFIC_PRODUCTS
              ? {
                  entities: [
                    { type: 'PRODUCT', entity_id: item.productIds },
                    { type: 'VARIANT', entity_id: item.variantIds }
                  ]
                }
              : {
                  entities: [{ type: 'COLLECTION', entity_id: item.collectionIds }]
                })
          }
        })
      })
    }
  }
  if (state.customerEligibility.type === CustomerEligibilityOptions.FIRST_TIME_CUSTOMERS) {
    executionConditions.push({
      type: 'ORDER_COUNT_ELIGIBILITY',
      user_order_cohort: {
        lifetime_min_order_count: 0,
        lifetime_max_order_count: 0
      }
    })
  }
  if (state.customerEligibility.type === CustomerEligibilityOptions.LIFETIME_ORDER_COUNT) {
    executionConditions.push({
      type: 'ORDER_COUNT_ELIGIBILITY',
      user_order_cohort: {
        lifetime_min_order_count: state.customerEligibility.minimumOrders,
        ...(state.customerEligibility.maximumOrders
          ? { lifetime_max_order_count: state.customerEligibility.maximumOrders }
          : {})
      }
    })
  }
  if (state.customerEligibility.type === CustomerEligibilityOptions.CUSTOMER_SEGMENT) {
    executionConditions.push({
      type: 'SPECIFIC_SEGMENTS',
      segment_ids: state.customerEligibility.segmentIds
    })
  }
  if (state.customerEligibility.type === CustomerEligibilityOptions.SPECIFIC_CUSTOMERS) {
    executionConditions.push({
      type: 'FILE_UPLOAD_ELIGIBILITY',
      segment_type: 'PHONE_NUMBER',
      file_meta: {
        file_url: state.customerEligibility.uploadedFile?.url,
        file_name: state.customerEligibility.uploadedFile?.name,
        size: state.customerEligibility.uploadedFile?.size
      }
    })
  }
  if (state.customerEligibility.type === CustomerEligibilityOptions.SPECIFIC_PINCODES) {
    executionConditions.push({
      type: 'FILE_UPLOAD_ELIGIBILITY',
      segment_type: 'PINCODE',
      file_meta: {
        file_url: state.customerEligibility.uploadedFile?.url,
        file_name: state.customerEligibility.uploadedFile?.name,
        size: state.customerEligibility.uploadedFile?.size
      }
    })
  }
  if (state.customerEligibility.type === CustomerEligibilityOptions.UTM_PARAMS) {
    executionConditions.push({
      type: 'UTM_PARAMS',
      utm_params: state.customerEligibility.utmData
    })
  }
  if (state.customerEligibility.type === CustomerEligibilityOptions.PINCODE_RTO_RISK) {
    executionConditions.push({
      type: 'RTO_CONDITIONS',
      score_type: 'PINCODE_RISK_SCORE',
      ...(state.customerEligibility.strategy === RtoRiskStrategyOptions.GREATER_THAN
        ? {
            min_value: state.customerEligibility.score
          }
        : {
            max_value: state.customerEligibility.score
          })
    })
  }
  if (state.customerEligibility.type === CustomerEligibilityOptions.CUSTOMER_RTO_RISK) {
    executionConditions.push({
      type: 'RTO_CONDITIONS',
      score_type: 'RTO_RISK_SCORE',
      ...(state.customerEligibility.strategy === RtoRiskStrategyOptions.GREATER_THAN
        ? {
            min_value: Number(state.customerEligibility.score) / 100
          }
        : {
            max_value: Number(state.customerEligibility.score) / 100
          })
    })
  }
  if (state.customerEligibility.type === CustomerEligibilityOptions.ORDER_RATE_LIMIT) {
    executionConditions.push({
      type: 'ORDER_RATE_LIMIT',
      payment_mode: state.customerEligibility.paymentMethod,
      max_orders: state.customerEligibility.maxOrders,
      window_days: state.customerEligibility.timePeriod
    })
  }

  if (state.performedAction.type === PerformedActionsOptions.REDEEM_REWARDS) {
    executionConditions.push({
      type: 'CUSTOMER_ACTION',
      customer_action: 'ON_APPLY_REWARDS'
    })
  }
  if (state.performedAction.type === PerformedActionsOptions.APPLY_DISCOUNTS) {
    executionConditions.push({
      type: 'CUSTOMER_ACTION',
      customer_action: 'ON_APPLY_DISCOUNTS',
      entity_ids: state.performedAction.appliedDiscounts
    })
  }
  return executionConditions
}

const createMetaData = (state: AutomationStateType) => {
  const metadata = {
    ...(state.cartConditions.type !== CartConditionOptions.NO_CONDITIONS &&
    state.cartConditions.selectedOption.type !== PurchaseOptions.ENTIRE_CART
      ? {
          prerequisites: state.cartConditions.selectedOption.quantityDistribution.quantityDistributionItems.map(
            item => item.catalogueMetadata
          )
        }
      : {}),
    ...(state.customerEligibility.type === CustomerEligibilityOptions.SPECIFIC_CUSTOMERS ||
    state.customerEligibility.type === CustomerEligibilityOptions.SPECIFIC_PINCODES
      ? {
          uploaded_file_data: state.customerEligibility.uploadedFile
        }
      : {}),
    channel_ids: state.channelIds
  }
  return metadata
}

export const createAutomationPayload = (state: AutomationStateType) => {
  const payload = {
    name: state.title,
    description: Boolean(state.description) ? state.description : '',
    type: getAutomationType(state.automatedAction.type),
    disabled_entities: getDisabledEntities(state),
    execution_conditions_v2: getExecutionConditions(state),
    is_active: state.isActive,
    start_time: state.activePeriod.startDate.getTime(),
    ...(state.activePeriod.endDate ? { end_time: state.activePeriod.endDate.getTime() } : {}),
    metadata: createMetaData(state)
  }
  return payload
}

export const getAutomationSortQueryParam = (sortOrder: AutomationSortOrderType) => {
  switch (sortOrder) {
    case 'NEWEST':
      return '&sort_by=created_at&sort_direction=DESC'
    case 'OLDEST':
      return '&sort_by=created_at&sort_direction=ASC'
    case 'NAME_ASC':
      return '&sort_by=name&sort_direction=ASC'
    case 'NAME_DESC':
      return '&sort_by=name&sort_direction=DESC'
  }
}

export const parseAutomationResponse = (response: any): AutomationStateType => {
  const getAutomatedAction = (): AutomatedActionType => {
    switch (response?.type) {
      case 'DISABLE_REWARDS':
        return { type: AutomatedActions.DISABLE_REWARDS }
      case 'DISABLE_PAYMENT':
        return { type: AutomatedActions.HIDE_PAYMENT_MODES, paymentMethodIds: response?.disabled_entities }
      default:
        return { type: AutomatedActions.DISABLE_REWARDS }
    }
  }

  const getPerformedAction = (): PerformedActionType => {
    const customerActionObject = response?.execution_conditions_v2?.find(
      (condition: any) => condition.type === 'CUSTOMER_ACTION'
    )
    if (!customerActionObject) {
      return { type: PerformedActionsOptions.NO_CONDITIONS }
    }
    if (customerActionObject?.customer_action === 'ON_APPLY_REWARDS') {
      return { type: PerformedActionsOptions.REDEEM_REWARDS }
    }
    if (customerActionObject?.customer_action === 'ON_APPLY_DISCOUNTS') {
      return {
        type: PerformedActionsOptions.APPLY_DISCOUNTS,
        appliedDiscounts: customerActionObject?.entity_ids,
        discountsMetadata: []
      }
    }
    return { type: PerformedActionsOptions.NO_CONDITIONS }
  }

  const getCustomerEligibility = (): CustomerEligibilityStateType => {
    const orderCountEligibilityObject = response?.execution_conditions_v2?.find(
      (condition: any) => condition.type === 'ORDER_COUNT_ELIGIBILITY'
    )
    if (orderCountEligibilityObject) {
      if (
        orderCountEligibilityObject?.user_order_cohort?.lifetime_min_order_count === 0 &&
        orderCountEligibilityObject?.user_order_cohort?.lifetime_max_order_count === 0
      ) {
        return { type: CustomerEligibilityOptions.FIRST_TIME_CUSTOMERS }
      }
      if (
        orderCountEligibilityObject?.user_order_cohort?.lifetime_min_order_count ||
        orderCountEligibilityObject?.user_order_cohort?.lifetime_max_order_count
      ) {
        return {
          type: CustomerEligibilityOptions.LIFETIME_ORDER_COUNT,
          minimumOrders: orderCountEligibilityObject?.user_order_cohort?.lifetime_min_order_count ?? 0,
          maximumOrders: orderCountEligibilityObject?.user_order_cohort?.lifetime_max_order_count || null
        }
      }
    }

    const segmentEligibilityObject = response?.execution_conditions_v2?.find(
      (condition: any) => condition.type === 'SPECIFIC_SEGMENTS'
    )
    if (segmentEligibilityObject) {
      return { type: CustomerEligibilityOptions.CUSTOMER_SEGMENT, segmentIds: segmentEligibilityObject?.segment_ids }
    }

    const fileUploadObject = response?.execution_conditions_v2?.find(
      (condition: any) => condition.type === 'FILE_UPLOAD_ELIGIBILITY'
    )
    if (fileUploadObject) {
      return {
        type:
          fileUploadObject?.segment_type === 'PHONE_NUMBER'
            ? CustomerEligibilityOptions.SPECIFIC_CUSTOMERS
            : CustomerEligibilityOptions.SPECIFIC_PINCODES,
        uploadedFile: response?.metadata?.uploaded_file_data
      }
    }

    const utmParamsObject = response?.execution_conditions_v2?.find((condition: any) => condition.type === 'UTM_PARAMS')
    if (utmParamsObject) {
      return {
        type: CustomerEligibilityOptions.UTM_PARAMS,
        utmData: utmParamsObject?.utm_params as Record<string, string[]>
      }
    }

    const rtoRiskObject = response?.execution_conditions_v2?.find(
      (condition: any) => condition.type === 'RTO_CONDITIONS'
    )
    if (rtoRiskObject) {
      const score =
        Number(Boolean(rtoRiskObject?.min_value) ? rtoRiskObject?.min_value : rtoRiskObject?.max_value) *
        (rtoRiskObject?.score_type === 'PINCODE_RISK_SCORE' ? 1 : 100)
      return {
        type:
          rtoRiskObject?.score_type === 'PINCODE_RISK_SCORE'
            ? CustomerEligibilityOptions.PINCODE_RTO_RISK
            : CustomerEligibilityOptions.CUSTOMER_RTO_RISK,
        strategy: Boolean(rtoRiskObject?.min_value)
          ? RtoRiskStrategyOptions.GREATER_THAN
          : RtoRiskStrategyOptions.LESS_THAN,
        score: score
      }
    }

    const orderRateLimitObject = response?.execution_conditions_v2?.find(
      (condition: any) => condition?.type === 'ORDER_RATE_LIMIT'
    )
    if (orderRateLimitObject) {
      return {
        type: CustomerEligibilityOptions.ORDER_RATE_LIMIT,
        maxOrders: orderRateLimitObject?.max_orders,
        timePeriod: orderRateLimitObject?.window_days,
        paymentMethod: orderRateLimitObject?.payment_mode
      }
    }
    return { type: CustomerEligibilityOptions.ALL_CUSTOMERS }
  }

  const cartConditionsObject = response?.execution_conditions_v2?.find(
    (condition: any) => condition.type === 'CART_CONDITIONS'
  )
  const state: AutomationStateType = {
    title: response?.name ?? '',
    description: response?.description ?? '',
    automatedAction: getAutomatedAction(),
    cartConditions: parseCartConditionsResponse(
      cartConditionsObject?.minimum_quantity,
      cartConditionsObject?.maximum_quantity,
      cartConditionsObject?.minimum_amount,
      cartConditionsObject?.maximum_amount,
      cartConditionsObject?.prerequisites,
      response?.metadata?.prerequisites
    ),
    performedAction: getPerformedAction(),
    customerEligibility: getCustomerEligibility(),
    activePeriod: {
      startDate: Boolean(response?.start_time)
        ? new Date(response?.start_time)
        : Boolean(response?.updated_at || response?.created_at)
        ? new Date(response?.updated_at ? response?.updated_at : response?.created_at)
        : new Date(),
      endDateEnabled: Boolean(response?.end_time),
      endDate: response?.end_time && new Date(response?.end_time)
    },
    isActive: response?.is_active,
    channelIds: response?.metadata?.channel_ids
  }
  return state
}

export const getAutomationTemplates = (response: any): AutomationTemplateType[] => {
  const templates: AutomationTemplateType[] = response?.data?.map((template: any): AutomationTemplateType => {
    return {
      id: template?.id,
      name: template?.name,
      title: template?.title,
      description: template?.description,
      image: template?.image,
      automation: {
        type: template?.automation?.type,
        label: getLabel(template?.automation?.type)
      }
    }
  })
  return templates
}

const getFormattedError = (message?: string): FlowErrorType => {
  return message ? { status: true, message } : { status: false }
}

export const getUnifiedCartConditionsError = (errors: any): CartConditionsErrorType => {
  const getQuantityDistributionError = (errors: any): Array<QuantityDistributionErrorType> => {
    return errors?.map((item: any): QuantityDistributionErrorType => {
      const selectedItemsMessage =
        item?.productIds?.message || item?.variantIds?.message || item?.collectionIds?.message

      return {
        minimumValue: getFormattedError(item?.minimumValue?.message),
        maximumValue: getFormattedError(item?.maximumValue?.message),
        selectedItems: getFormattedError(selectedItemsMessage)
      }
    })
  }
  return {
    minimumValue: getFormattedError(errors?.selectedOption?.minimumValue?.message),
    maximumValue: getFormattedError(errors?.selectedOption?.maximumValue?.message),
    quantityDistribution: getQuantityDistributionError(
      errors?.selectedOption?.quantityDistribution?.quantityDistributionItems
    )
  }
}

export const getUnifiedCustomerEligibilityError = (errors: any): CustomerEligibilityErrorType => {
  return {
    minimumOrders: getFormattedError(errors?.minimumOrders?.message),
    maximumOrders: getFormattedError(errors?.maximumOrders?.message),
    segmentIds: getFormattedError(errors?.segmentIds?.message),
    utmData: getFormattedError(errors?.utmData?.message),
    score: getFormattedError(errors?.score?.message),
    paymentMethod: getFormattedError(errors?.paymentMethod?.message),
    maxOrders: getFormattedError(errors?.maxOrders?.message),
    timePeriod: getFormattedError(errors?.timePeriod?.message),
    uploadedFile: getFormattedError(errors?.uploadedFile?.message),
    fileMeta: getFormattedError(errors?.fileMeta?.fileUrl?.message)
  }
}

export type CartConditionsErrorType = {
  minimumValue: FlowErrorType
  maximumValue: FlowErrorType
  quantityDistribution: QuantityDistributionErrorType[]
}

export type QuantityDistributionErrorType = {
  minimumValue: FlowErrorType
  maximumValue: FlowErrorType
  selectedItems: FlowErrorType
}
